import { Injectable } from "@angular/core";
import { EventDataModel, EventViewModel } from "../../shared/models/event.model";
import { CoreLogService } from "../../services/log.service";
import { LikeService } from "../../services/like.service";
import { Observable } from "rxjs";
import { ContentType, ImageOrientation } from "../../shared/contentTypes";
import * as moment from "moment";
import DateManager from "../../shared/dateManager";
import { CircularInjectionHelper } from "../../services/circularInjectionHelper";
import { UserHelperService } from "../../services/userHelper.service";
import { IObjectObserver } from "../../services/IObjectObserver";
import { ObjectObserverFactory } from "../../services/objectObserverFactory";

@Injectable()
export class EventService {

  private _eventObsServer: IObjectObserver<EventDataModel>;

  constructor(private _coreLogService: CoreLogService,
    private _likeService: LikeService,
    private _userHelperService: UserHelperService,
    objObsInjectHelper: CircularInjectionHelper) {
    this._eventObsServer = ObjectObserverFactory.GetObjectObserverByInjectionHelper<EventDataModel>(ContentType.Event, objObsInjectHelper);
  }

  public get ObserveChanges(): Observable<(string | number)[]> {
    return this._eventObsServer.ObserveChanges;
  }

  /**
   * Getting the list of EventDataModel
   * Create eventViewModelList
   * @result return a promise that resolves a list of eventViewModel
   */
  public GetEventViewModel(): Promise<EventViewModel[]> {
    const me = this;
    let isTestUser: boolean = false;
    return new Promise<EventViewModel[]>((resolve, reject) => {
      isTestUser = me._userHelperService.IsTestUser;
      me._eventObsServer.GetObjectsByFilter(model => model.release && (model.public || isTestUser))
        .then((event) => {
          me.mapDataModelListToViewModelList(event)
            .then(resolve)
            .catch((e) => {
              me._coreLogService.error("EventService.GetEventViewModel", "mapDataModelListToViewModelList", e);
              reject();
            });
        })
        .catch((e) => {
          me._coreLogService.error("EventService.GetEventViewModel", "GetEventViewModel", e);
          reject();
        });
    });
  }

  // Mapper
  /**
  * map a EventDataModel to EventViewModel
  * @param model Get a EventDataModel for mapping
  * @result return a EventViewModel
  */
  private async mapDataModelToViewModel(model: EventDataModel): Promise<EventViewModel> {
    const me = this;
    const now: moment.Moment = moment();
    const viewModel = new EventViewModel();
    viewModel.id = model.id;
    viewModel.momentStartDate = model.date;
    viewModel.startDate = DateManager.MomentDateTimeFormatter(model.date, true, false);
    viewModel.startDayMonth = viewModel.startDate.slice(0, 6);
    viewModel.startYear = viewModel.startDate.slice(6);
    viewModel.endDate = DateManager.MomentDateTimeFormatter(model.end_date, model.has_end_date_ymd, false);
    viewModel.endDayMonth = viewModel.endDate.slice(0, 6);
    viewModel.endYear = viewModel.endDate.slice(6);
    viewModel.isExpired = (now.valueOf() - model.end_date.valueOf() >= 0) ? true : false;
    viewModel.label_de = model.label_de;
    viewModel.label_en = model.label_en;
    viewModel.text_de = model.text_de.replace("\n","<br>");;
    viewModel.text_en = model.text_en.replace("\n","<br>");;
    viewModel.image_photo = ContentType.Event + "/" + model.id + "_photo_" + ImageOrientation.Landscape + "/" + model.last_image_update_at_ymd + "/" + model.last_image_update_at_hms;
    try {
      viewModel.like = await me._likeService.HasLike(ContentType.Event, model.id);
    } catch (error) {
      me._coreLogService.error("EventService.mapDataModelToViewModel", "HasLike try catch", error);
    }
    viewModel.locations = model.locations;

    return Promise.resolve(viewModel);
  }

  /**
  * map a list of EventDataModel to a list of EventViewModel
  * @param model Get a list of EventDataModel for mapping
  * @result return a list of EventViewModel
  */
  private mapDataModelListToViewModelList(modelList: EventDataModel[]): Promise<EventViewModel[]> {
    const me = this;
    if (modelList && modelList.length > 0) {
      return Promise.all(modelList.map((model) => me.mapDataModelToViewModel(model)));
    } else {
      return Promise.resolve(null);
    }
  }
}
